import {AgentListItems} from '@github-ui/agent-sessions/components/new-task-chat-input/AgentListItems'
import {useTopLevelAgents} from '@github-ui/agent-sessions/hooks/useTopLevelAgents'
import type {SelectedAgent} from '@github-ui/agent-sessions/types/session'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {FileCheckIcon} from '@github-ui/pacer/CustomIcon'
import {CopilotIcon, PlusIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Button, LinkButton} from '@primer/react'
import {useMemo} from 'react'

import {useCustomAgentsQuery} from '../../hooks/use-custom-agents-query'
import styles from './AgentSelector.module.css'
import {IconButtonSelector} from './IconButtonSelector'

type AgentSelectorProps = {
  nwo: string | undefined
  branch: string | undefined
  selectedAgent: SelectedAgent
  onSelectAgent: (agent: SelectedAgent) => void
}

export function AgentSelector({nwo, branch, selectedAgent, onSelectAgent}: AgentSelectorProps) {
  const {data: customAgents = [], isLoading: isLoadingCustom} = useCustomAgentsQuery(nwo)
  const use3pAgentInfo = isFeatureEnabled('copilot_3p_agent_info')
  const {data: topLevelAgents = [], isLoading: isLoadingTopLevel} = useTopLevelAgents(nwo, use3pAgentInfo)

  // Default to "Copilot" if feature flag is enabled and no agent is selected
  const isEmptyAgent = (agent: SelectedAgent) => !agent?.topLevelAgentId && !agent?.subAgentSlug
  const effectiveSelectedAgent = use3pAgentInfo && isEmptyAgent(selectedAgent) ? 'Copilot' : selectedAgent

  const isLoading = isLoadingCustom || isLoadingTopLevel
  const agents = [...topLevelAgents, ...customAgents]
  const selectedAgentObj = agents.find(agent => {
    if (typeof effectiveSelectedAgent === 'string') {
      return agent.display_name === effectiveSelectedAgent
    }
    if ('id' in agent && agent.id) {
      return agent.id === effectiveSelectedAgent.topLevelAgentId
    } else if ('name' in agent) {
      return agent.name === effectiveSelectedAgent.subAgentSlug
    }
  })

  const selectedAgentName = (() => {
    if (typeof effectiveSelectedAgent === 'string') {
      return effectiveSelectedAgent
    }
    if (!selectedAgentObj) {
      return undefined
    }
    return selectedAgentObj.display_name || ('name' in selectedAgentObj ? selectedAgentObj.name : undefined)
  })()

  const newFileUrl = useMemo(() => {
    const params = new URLSearchParams({
      filename: '.github/agents/my-agent.agent.md',
      value:
        '---\n# Fill in the fields below to create a basic custom agent for your repository.\n# The Copilot CLI can be used for local testing: https://gh.io/customagents/cli\n# To make this agent available, merge this file into the default repository branch.\n# For format details, see: https://gh.io/customagents/config\n\nname:\ndescription:\n---\n\n# My Agent\n\nDescribe what your agent does here...',
    })
    return `/${nwo}/new/${branch}?${params.toString()}`
  }, [nwo, branch])

  const ariaLabel = selectedAgentName
    ? `${use3pAgentInfo ? 'Agent' : 'Custom agent'}: ${selectedAgentName}`
    : `Select ${use3pAgentInfo ? 'an agent' : 'a custom agent'}`

  const disabled = !nwo || !branch || isLoading

  return (
    <ActionMenu>
      <ActionMenu.Anchor>
        {use3pAgentInfo && selectedAgentObj && 'avatar_url' in selectedAgentObj ? (
          <Button aria-label={ariaLabel} disabled={disabled} className={styles.avatarButton}>
            <GitHubAvatar src={selectedAgentObj.avatar_url} size={24} alt="" />
          </Button>
        ) : (
          <IconButtonSelector
            icon={CopilotIcon}
            selectedIcon={FileCheckIcon}
            aria-label={ariaLabel}
            selected={Boolean(selectedAgentObj)}
            disabled={disabled}
          />
        )}
      </ActionMenu.Anchor>
      <ActionMenu.Overlay width="auto">
        {topLevelAgents.length === 0 && customAgents.length === 0 ? (
          <EmptyState newFileUrl={newFileUrl} />
        ) : (
          <ActionList>
            <AgentListItems
              topLevelAgents={topLevelAgents}
              customAgents={customAgents}
              selectedAgent={selectedAgent}
              onSelectAgent={onSelectAgent}
              showTopLevelAgents={use3pAgentInfo}
            />

            <ActionList.Divider />

            <ActionList.Group selectionVariant={false}>
              <ActionList.LinkItem href={newFileUrl} target="_blank">
                <ActionList.LeadingVisual>
                  <PlusIcon />
                </ActionList.LeadingVisual>
                Create a custom agent
              </ActionList.LinkItem>
            </ActionList.Group>
          </ActionList>
        )}
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}
const EmptyState = ({newFileUrl}: {newFileUrl: string}) => (
  <div className={styles.emptyStateContainer}>
    <h3 className={styles.emptyStateTitle}>This repository has no custom agents</h3>
    <p className={styles.emptyStateDescription}>
      Custom agents are reusable instructions and tools in your repository.
    </p>
    <LinkButton href={newFileUrl}>Create a custom agent</LinkButton>
  </div>
)

try{ AgentSelector.displayName ||= 'AgentSelector' } catch {}
try{ EmptyState.displayName ||= 'EmptyState' } catch {}